import type { CalendarIcsPullConfig, CalendarVisibilityMode } from '@/api/mcp-appointments/types';
import { getGatewayUrl } from '@/services/http-client';

/** Public base URL passed as baseUrl when creating export ICS feed links. */
export const calendarExportPublicBaseUrl = (): string =>
  `${getGatewayUrl()}/mcp-appointments`;

/** Reads pull sync config from feed metadata (metadata.icsPull). */
export function getIcsPullConfig(metadata: Record<string, unknown> | null | undefined): CalendarIcsPullConfig | null {
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }

  const raw = metadata.icsPull;
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const config = raw as Record<string, unknown>;
  if (typeof config.enabled !== 'boolean') {
    return null;
  }

  return {
    enabled: config.enabled,
    intervalMinutes: typeof config.intervalMinutes === 'number' ? config.intervalMinutes : 10,
    markMissingAsDeleted: typeof config.markMissingAsDeleted === 'boolean' ? config.markMissingAsDeleted : true,
    syncWindowPastDays: typeof config.syncWindowPastDays === 'number' ? config.syncWindowPastDays : 30,
    syncWindowFutureDays: typeof config.syncWindowFutureDays === 'number' ? config.syncWindowFutureDays : 180,
    requestTimeoutMs: typeof config.requestTimeoutMs === 'number' ? config.requestTimeoutMs : 15000,
    maxEventsPerSync: typeof config.maxEventsPerSync === 'number' ? config.maxEventsPerSync : 5000,
    updatedAt: typeof config.updatedAt === 'string' ? config.updatedAt : undefined
  };
}

/** Resolves visibility mode from API field or feed metadata. */
export function getFeedVisibilityMode(
  feed: { visibilityMode?: CalendarVisibilityMode; metadata?: Record<string, unknown> | null }
): CalendarVisibilityMode {
  if (feed.visibilityMode === 'PRIVACY_MODE' || feed.visibilityMode === 'FULL_VISIBILITY') {
    return feed.visibilityMode;
  }

  if (!feed.metadata || typeof feed.metadata !== 'object') {
    return 'FULL_VISIBILITY';
  }

  const raw = feed.metadata.visibilityMode;
  return raw === 'PRIVACY_MODE' ? 'PRIVACY_MODE' : 'FULL_VISIBILITY';
}

export function isPrivacyMode(mode: CalendarVisibilityMode): boolean {
  return mode === 'PRIVACY_MODE';
}
